import React, { useCallback, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Box, Typography } from '@mui/material';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useDataProvider,
  DatePicker,
  FieldWrapperVertical,
  TimePicker,
  useUserAccess,
  SnackbarApiErrorMessage,
  useLocalization,
  useDialog,
  Select,
  useSnackbar,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import formatDateTime from '@product-framework/utils/formatDateTime';
import {
  buildAcceptanceLocationYupFields,
  getAcceptanceLocationIdFromForm,
  SERVICE_PROVIDER_LOCATION_ID_PARAM,
} from '@product-framework/utils/acceptJobLocation';
import usePendingAcceptanceLocations from '@product-framework/hooks/usePendingAcceptanceLocations';
import AcceptanceLocationFormSection from './AcceptanceLocationFormSection';
import PendingAcceptanceDialogBody from './PendingAcceptanceDialogBody';
import IconLibrary from '../IconLibrary';
import DialogCloseButton from './DialogCloseButton';

function buildAcceptJobSchema({
  formatMessage,
  countOfCSR,
  singleCSRButInPast,
  requireAcceptanceLocation,
}) {
  const loc = requireAcceptanceLocation
    ? buildAcceptanceLocationYupFields(formatMessage)
    : {};

  if (countOfCSR > 1 || (countOfCSR === 1 && singleCSRButInPast)) {
    return yup.object().shape({
      customerServiceRequests: yup.string().required(
        formatMessage({
          id: 'CSR_DATE_REQUIRED',
          defaultMessage: 'Appointment Date is required',
        }),
      ),
      appointmentDate: yup
        .string()
        .test(
          'required-if-other',
          'Date is required when "Other" is selected',
          (value, { parent }) => {
            const { customerServiceRequests: csr } = parent;
            if (csr === 'OTHER') {
              return value != null && value !== '';
            }
            return true;
          },
        )
        .test(
          'date-not-in-past',
          'Date cannot be in the past',
          (value, { parent }) => {
            const { customerServiceRequests: csr } = parent;
            if (csr === 'OTHER' && value) {
              return !dayjs(value).isBefore(dayjs(), 'day');
            }
            return true;
          },
        ),
      ...loc,
    });
  }

  if (countOfCSR === 0) {
    return yup.object().shape({
      appointmentDate: yup
        .string()
        .required(
          formatMessage({
            id: 'APPOINTMENT_DATE_REQUIRED',
            defaultMessage: 'Date is required',
          }),
        )
        .test(
          'is-date-in-past',
          formatMessage({
            id: 'APPOINTMENT_DATE_PAST',
            defaultMessage: 'Date cannot be in the past',
          }),
          value => !dayjs(value).isBefore(dayjs(), 'day'),
        ),
      ...loc,
    });
  }

  if (requireAcceptanceLocation) {
    return yup.object().shape(loc);
  }

  return null;
}

function AcceptJobFormInner({
  schema,
  defaultValues,
  countOfCSR,
  singleCSRButInPast,
  customerServiceRequests,
  dateOptions,
  showLocationField,
  showNoDefaultLocationMessage,
  formatMessage,
  checkAndInjectDisabledForPastDates,
  onSubmit,
  onCancel,
}) {
  const methods = useForm({
    ...(schema && { resolver: yupResolver(schema) }),
    defaultValues,
  });

  const {
    handleSubmit,
    control: innerControl,
    formState: { errors: innerErrors },
  } = methods;

  const customerServiceRequestsValue = useWatch({
    control: innerControl,
    name: 'customerServiceRequests',
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box>
            <AcceptanceLocationFormSection
              showNoDefaultLocationMessage={showNoDefaultLocationMessage}
              showLocationField={showLocationField}
              formatMessage={formatMessage}
              errors={innerErrors}
            />
            {countOfCSR === 0 && (
              <Grid2 container spacing={1}>
                <Grid2 xs={4}>
                  <FieldWrapperVertical
                    field={{
                      name: 'appointmentDate',
                      label: {
                        localeId: 'APOINTMENT_DATE',
                        defaultMessage: 'Appointment Date',
                      },
                    }}
                    errors={innerErrors}>
                    <DatePicker name="appointmentDate" disablePast />
                  </FieldWrapperVertical>
                </Grid2>
                <Grid2 xs={3}>
                  <Controller
                    name="serviceDateAfterHour"
                    control={innerControl}
                    render={({ field }) => (
                      <FieldWrapperVertical
                        field={{
                          name: 'serviceDateAfterHour',
                          label: {
                            localeId: 'SERVICE_DATE_AFTER_HOUR_LABEL',
                            defaultMessage: 'After',
                          },
                        }}>
                        <TimePicker {...field} />
                      </FieldWrapperVertical>
                    )}
                  />
                </Grid2>
                <Grid2 xs={3}>
                  <Controller
                    name="serviceDateBeforeHour"
                    control={innerControl}
                    render={({ field }) => (
                      <FieldWrapperVertical
                        field={{
                          name: 'serviceDateBeforeHour',
                          label: {
                            localeId: 'SERVICE_DATE_BEFORE_HOUR_LABEL',
                            defaultMessage: 'Before',
                          },
                        }}>
                        <TimePicker {...field} />
                      </FieldWrapperVertical>
                    )}
                  />
                </Grid2>
              </Grid2>
            )}
            {countOfCSR === 1 && !singleCSRButInPast && (
              <Typography textAlign="center">
                Do you want to accept the job for{' '}
                {formatDateTime(
                  customerServiceRequests[0].requestDate,
                  customerServiceRequests[0].afterHour,
                  customerServiceRequests[0].beforeHour,
                )}
                ?
              </Typography>
            )}
            {((countOfCSR === 1 && singleCSRButInPast) || countOfCSR > 1) && (
              <Box>
                <Box>
                  <Controller
                    name="customerServiceRequests"
                    control={innerControl}
                    render={({ field }) => {
                      const { value, ...rest } = field;
                      return (
                        <FieldWrapperVertical
                          field={{
                            name: 'customerServiceRequests',
                            label: {
                              localeId: 'APOINTMENT_DATE',
                              defaultMessage: 'Appointment Date',
                            },
                          }}
                          errors={innerErrors}>
                          <Select
                            {...rest}
                            options={checkAndInjectDisabledForPastDates(
                              dateOptions,
                            )}
                          />
                        </FieldWrapperVertical>
                      );
                    }}
                  />
                </Box>
                {customerServiceRequestsValue === 'OTHER' && (
                  <Box sx={{ mt: 2 }}>
                    <Grid2 container spacing={1}>
                      <Grid2 xs={5}>
                        <FieldWrapperVertical
                          field={{
                            name: 'appointmentDate',
                            label: {
                              localeId: 'APOINTMENT_DATE',
                              defaultMessage: 'Date',
                            },
                          }}
                          errors={innerErrors}>
                          <DatePicker name="appointmentDate" disablePast />
                        </FieldWrapperVertical>
                      </Grid2>
                      <Grid2 xs={3.5}>
                        <Controller
                          name="serviceDateAfterHour"
                          control={innerControl}
                          render={({ field }) => (
                            <FieldWrapperVertical
                              field={{
                                name: 'serviceDateAfterHour',
                                label: {
                                  localeId: 'SERVICE_DATE_AFTER_HOUR_LABEL',
                                  defaultMessage: 'After',
                                },
                              }}>
                              <TimePicker {...field} />
                            </FieldWrapperVertical>
                          )}
                        />
                      </Grid2>
                      <Grid2 xs={3.5}>
                        <Controller
                          name="serviceDateBeforeHour"
                          control={innerControl}
                          render={({ field }) => (
                            <FieldWrapperVertical
                              field={{
                                name: 'serviceDateBeforeHour',
                                label: {
                                  localeId: 'SERVICE_DATE_BEFORE_HOUR_LABEL',
                                  defaultMessage: 'Before',
                                },
                              }}>
                              <TimePicker {...field} />
                            </FieldWrapperVertical>
                          )}
                        />
                      </Grid2>
                    </Grid2>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions display="flex" sx={{ justifyContent: 'center', my: 2 }}>
          <Button
            type="Submit"
            variant="contained"
            color="primary"
            startIcon={<IconLibrary.Accept />}>
            {formatMessage({ id: 'ACCEPT', defaultMessage: 'Accept' })}
          </Button>
          <Button
            type="button"
            onClick={onCancel}
            variant="contained"
            color="secondary"
            startIcon={<IconLibrary.Cancel />}>
            {formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' })}
          </Button>
        </DialogActions>
      </form>
    </FormProvider>
  );
}

function AcceptJobDialog({ open, onClose, jobInfo, dispatchEventId }) {
  const { formatMessage } = useLocalization();

  const userAccess = useUserAccess();
  const snackbar = useSnackbar();
  const serviceProviderId = userAccess?.company?.id;
  const { confirmationRef } = useDialog();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, put, get } = useDataProvider(provider);
  const { id: jobId, customerServiceRequests = [], serviceJobNumber } = jobInfo;

  const countOfCSR = Array.isArray(customerServiceRequests)
    ? customerServiceRequests?.length
    : 0;

  let singleCSRButInPast = false;

  const dateOptions = customerServiceRequests?.map(date => {
    return {
      label: formatDateTime(
        date?.requestDate,
        date?.afterHour,
        date?.beforeHour,
      ),
      value: JSON.stringify(date),
    };
  });

  if (countOfCSR === 1) {
    const now = dayjs();
    if (
      dayjs(JSON.parse(dateOptions[0].value)?.requestDate).isBefore(now, 'day')
    ) {
      singleCSRButInPast = true;
    }
  }

  const [loading, setLoading] = useState(false);

  const {
    locationsLoading,
    activeCount,
    singleLocationId,
    showLocationField,
    defaultLocationOption,
    showNoDefaultLocationMessage,
    hasNoActiveLocations,
    formMountKey,
  } = usePendingAcceptanceLocations({
    open,
    serviceProviderId,
    get,
    getApiUrl,
    jobId,
    dispatchEvent: jobInfo?.dispatchEvent,
  });

  const schema = useMemo(
    () =>
      buildAcceptJobSchema({
        formatMessage,
        countOfCSR,
        singleCSRButInPast,
        requireAcceptanceLocation: showLocationField,
      }),
    [formatMessage, countOfCSR, singleCSRButInPast, showLocationField],
  );

  const formDefaultValues = useMemo(
    () => ({
      serviceProviderId,
      ...(defaultLocationOption != null && {
        acceptanceLocation: defaultLocationOption,
      }),
    }),
    [serviceProviderId, defaultLocationOption],
  );

  const getJobDispatchId = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/pending-acceptance/${jobId}`,
    );
    if (response.ok) {
      return response?.data?.dispatchEvent?.id;
    }
    return null;
  };

  const onFormSubmit = async data => {
    setLoading(true);
    const dispatchEventValue = dispatchEventId || (await getJobDispatchId());
    if (!dispatchEventValue) {
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'ACCEPT_JOB_FAILURE',
          defaultMessage: 'Error: Could not accept the job',
        }),
      });
      setLoading(false);
      return;
    }

    const locationId = getAcceptanceLocationIdFromForm(data, {
      activeCount,
      singleLocationId,
    });

    if (activeCount === 0 || !locationId) {
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'ACCEPT_JOB_LOCATION_REQUIRED',
          defaultMessage: 'Location is required',
        }),
      });
      setLoading(false);
      return;
    }

    let body = {};

    if (countOfCSR === 0) {
      body = {
        serviceDate: data.appointmentDate,
        serviceDateAfterHour: data.serviceDateAfterHour,
        serviceDateBeforeHour: data.serviceDateBeforeHour,
      };
    } else if (countOfCSR === 1 && !singleCSRButInPast) {
      body = {
        serviceDate: customerServiceRequests[0].requestDate,
        serviceDateAfterHour: customerServiceRequests[0].afterHour,
        serviceDateBeforeHour: customerServiceRequests[0].beforeHour,
      };
    } else if (countOfCSR > 1 || (countOfCSR === 1 && singleCSRButInPast)) {
      if (data?.customerServiceRequests === 'OTHER') {
        body = {
          serviceDate: data.appointmentDate,
          serviceDateAfterHour: data.serviceDateAfterHour,
          serviceDateBeforeHour: data.serviceDateBeforeHour,
        };
      } else {
        const csrDate = JSON.parse(data?.customerServiceRequests);
        body = {
          serviceDate: csrDate.requestDate,
          serviceDateAfterHour: csrDate.afterHour,
          serviceDateBeforeHour: csrDate.beforeHour,
        };
      }
    }

    const finalBody = {
      dispatchEventId: dispatchEventValue,
      ...body,
      jobRecordVersion: jobInfo.recordVersion,
    };

    const query = new URLSearchParams();
    query.set(SERVICE_PROVIDER_LOCATION_ID_PARAM, locationId);
    const response = await put(
      `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/pending-acceptance/${jobId}/accept?${query.toString()}`,
      finalBody,
    );

    if (response.ok) {
      confirmationRef.current.init({
        message: 'Job was accepted successfully',
        positiveButtonText: 'Close',
        width: 360,
        title: 'Job Accepted',
        onChange: () => {
          confirmationRef.current.setOpen(false);
          onClose(nanoid());
        },
      });
      confirmationRef.current.setOpen(true);
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    }
    setLoading(false);
  };

  const checkAndInjectDisabledForPastDates = useCallback(options => {
    const now = dayjs();
    let allDisabled = true;
    const finalOptions = options.map(opt => {
      const isDisabled = dayjs(JSON.parse(opt.value)?.requestDate).isBefore(
        now,
        'day',
      );
      if (!isDisabled) {
        allDisabled = false;
      }
      return {
        ...opt,
        disabled: isDisabled,
      };
    });
    if (allDisabled) {
      finalOptions.push({
        label: 'Other',
        value: 'OTHER',
      });
    }
    return finalOptions;
  }, []);

  const onCancel = useCallback(() => onClose(null), [onClose]);

  const dialogBody = (
    <PendingAcceptanceDialogBody
      loading={loading}
      locationsLoading={locationsLoading}
      hasNoActiveLocations={hasNoActiveLocations}
      formatMessage={formatMessage}
      onCancel={onCancel}>
      <AcceptJobFormInner
        key={formMountKey}
        schema={schema}
        defaultValues={formDefaultValues}
        countOfCSR={countOfCSR}
        singleCSRButInPast={singleCSRButInPast}
        customerServiceRequests={customerServiceRequests}
        dateOptions={dateOptions}
        showLocationField={showLocationField}
        showNoDefaultLocationMessage={showNoDefaultLocationMessage}
        formatMessage={formatMessage}
        checkAndInjectDisabledForPastDates={checkAndInjectDisabledForPastDates}
        onSubmit={onFormSubmit}
        onCancel={onCancel}
      />
    </PendingAcceptanceDialogBody>
  );

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose(null);
      }}
      maxWidth="sm"
      fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.AcceptServiceJob />
        {formatMessage({
          id: 'ACCEPT_JOB',
          defaultMessage: 'Accept Job',
        })}
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {serviceJobNumber}
        </Typography>
        <DialogCloseButton
          disabled={loading}
          onClose={() => {
            onClose(null);
          }}
        />
      </DialogTitle>
      {dialogBody}
    </Dialog>
  );
}

export default AcceptJobDialog;
